/**
 * Formatea un número con separadores de miles y decimales
 * @param value - El número a formatear
 * @param decimals - Número de decimales (default: 0)
 * @param currency - Moneda para agregar símbolo (opcional)
 * @returns String formateado
 */
export const formatNumber = (
  value: number | string | null | undefined,
  decimals: number = 0,
  currency?: string
): string => {
  // Manejar valores nulos o undefined
  if (value === null || value === undefined || value === '') {
    return currency ? getCurrencySymbol(currency) + ' 0' : '0';
  }

  // Convertir a número
  const num = typeof value === 'string' ? parseFloat(value) : value;

  // Verificar si es un número válido
  if (isNaN(num)) {
    return currency ? getCurrencySymbol(currency) + ' 0' : '0';
  }

  // Formatear el número
  const formatted = new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true
  }).format(num);

  // Agregar símbolo de moneda si es necesario
  if (currency) {
    const symbol = getCurrencySymbol(currency);
    return `${symbol} ${formatted}`;
  }

  return formatted;
};

/**
 * Obtiene el símbolo de la moneda
 */
export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    RUB: '₽',
    USD: '$',
    EUR: '€',
  };
  return symbols[currency] || '₽';
};

/**
 * Formatea un número como moneda
 */
export const formatCurrency = (
  value: number | string | null | undefined,
  currency: string = 'RUB'
): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num as number)) {
    return getCurrencySymbol(currency) + ' 0';
  }

  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(num as number);
};

/**
 * Formatea un número como porcentaje
 */
export const formatPercentage = (value: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(value / 100);
};

/**
 * Parsea un string formateado a número
 */
export const parseFormattedNumber = (formatted: string): number => {
  // Remover todo excepto números, punto decimal y signo negativo
  const clean = formatted.replace(/[^0-9.,-]/g, '');
  // Reemplazar coma decimal por punto
  const normalized = clean.replace(',', '.');
  return parseFloat(normalized);
};

/**
 * Formatea un número con notación abreviada (k, M, B)
 * Ejemplos:
 * - 1,000 → "1k"
 * - 1,500 → "1.5k"  
 * - 1,000,000 → "1M"
 * - 1,500,000 → "1.5M"
 */
export const formatCompactNumber = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const absValue = Math.abs(value);
  
  if (absValue >= 1_000_000_000) {
    return (value / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (absValue >= 1_000_000) {
    return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (absValue >= 1_000) {
    return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return value.toString();
};

/**
 * Formatea número para eje Y (abreviado)
 */
export const formatYAxisTick = (value: number): string => {
  if (value === 0) return '0';
  
  const absValue = Math.abs(value);
  
  if (absValue >= 1_000_000) {
    return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (absValue >= 1_000) {
    return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return value.toString();
};